import { useParams } from "react-router-dom";

import { TabSideNavigationItem } from "@shared";

import { UserPermission } from "@/common/contexts/current-user-context/CurrentUserContextProvider.types";
import withAuthorization from "@/common/hocs/with-authorization/withAuthorization";
import { useRedirectWhenMissing } from "@/common/hooks/useRedirectWhenMissing";
import { DetailPageLayout } from "@/pages/system-settings/components/detail-page/DetailPageLayout";
import { useActiveDetailTab } from "@/pages/system-settings/components/detail-page/hooks/useActiveDetailTab";
import {
  SystemSettingsTab,
  SystemSettingsVolunteerTab,
  VolunteerByIdParams,
  systemSettingsPath,
} from "@/router/SystemSettingsRoutes";
import { useTranslation } from "@/translations";

import { useVolunteer } from "./hooks/useVolunteer";

function VolunteerDetailInner() {
  const { t } = useTranslation();
  const { id } = useParams<VolunteerByIdParams>();
  const { volunteer, isLoading } = useVolunteer();
  const activeTab = useActiveDetailTab(SystemSettingsTab.Volunteers, SystemSettingsVolunteerTab);

  const volunteersListPath = systemSettingsPath.byTab(SystemSettingsTab.Volunteers);
  useRedirectWhenMissing(isLoading, volunteer, volunteersListPath);

  if (!volunteer || !id) {
    return null;
  }

  const navigationItems: TabSideNavigationItem[] = [
    {
      key: SystemSettingsVolunteerTab.PersonalData,
      label: t("volunteers.tabs.personalData"),
      to: systemSettingsPath.byVolunteerTab({ id, tab: SystemSettingsVolunteerTab.PersonalData }),
    },
    {
      key: SystemSettingsVolunteerTab.AssignedClients,
      label: t("volunteers.tabs.assignedClients"),
      to: systemSettingsPath.byVolunteerTab({ id, tab: SystemSettingsVolunteerTab.AssignedClients }),
    },
    {
      key: SystemSettingsVolunteerTab.Access,
      label: t("volunteers.tabs.access"),
      to: systemSettingsPath.byVolunteerTab({ id, tab: SystemSettingsVolunteerTab.Access }),
    },
  ];

  return (
    <DetailPageLayout
      title={[volunteer.firstName, volunteer.lastName].filter(Boolean).join(" ")}
      role={UserPermission.Volunteer}
      backTo={volunteersListPath}
      backAriaLabel={t("volunteers.backAria")}
      navigationItems={navigationItems}
      activeTabKey={activeTab}
      context={volunteer}
    />
  );
}

export const VolunteerDetail = withAuthorization(VolunteerDetailInner, UserPermission.Supervisor);
